import { gql, useLazyQuery, useMutation } from "@apollo/client";
import PropTypes from "prop-types";
import { useContext, useEffect, useState, useCallback } from "react";

// @mui material components
import {
  Card,
  createFilterOptions,
  Fade,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Autocomplete,
  TextField,
} from "@mui/material";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { SEARCH_UNIFIED_USERS, selectUsersBySource } from "services/unifiedUsers";
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "70%", xxl: "50%" },
  overflow: "auto",
  p: 3,
};

const ADD_USUARIO_VIP = gql`
  mutation saveUsuarioVip($input: UsuarioVipInput!) {
    saveUsuarioVip(input: $input) {
      id
    }
  }
`;

const clienteInicial = {
  dni: "",
  nombre: "",
  apellido: "",
};

export default function ModalAdd({ open, handleClose, refetch }) {
  const { handleSnackbar } = useContext(MessageManager);
  const [cliente, setCliente] = useState(clienteInicial);
  const [tipoClientes, setTipoClientes] = useState("Comedor");
  const [clientes, setClientes] = useState([]);
  const [saveUsuarioVip, { loading }] = useMutation(ADD_USUARIO_VIP);
  const [debouncing, setDebouncing] = useState(false);
  const [search, setSearch] = useState("");

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const filterOptions = createFilterOptions({
    stringify: ({ apellido, nombre, dni }) => `${apellido} ${nombre} ${dni}`,
    limit: 15,
  });

  const [getUnifiedUsers] = useLazyQuery(SEARCH_UNIFIED_USERS, {
    fetchPolicy: "network-only",
  });

  const handleChangeTipo = (e) => {
    setTipoClientes(e.target.value);
    setCliente(clienteInicial);
    setClientes([]);
  };
  const handleSearchDebounce = useCallback(
    debounce(async (searchTerm, source) => {
      try {
        const { data: result } = await getUnifiedUsers({
          variables: { searchTerm },
        });
        setClientes(selectUsersBySource(result?.searchUnifiedUsers, source));
      } finally {
        setDebouncing(false);
      }
    }, 1000),
    [getUnifiedUsers]
  );
  useEffect(() => {
    if (open) {
      const source = tipoClientes === "Alumno" ? "Alumnos" : tipoClientes === "Comedor" ? "Comedor" : "Compras";
      handleSearchDebounce(search, source);
    }
  }, [search, open, tipoClientes, handleSearchDebounce]);
  const handleSearchChange = (e, value) => {
    if (value.length > 1) {
      setDebouncing(true);
    }
    setSearch(value);
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" mb={2}>
              <SoftBox>
                <SoftTypography variant="h6">
                  Agregar usuario vip
                </SoftTypography>
              </SoftBox>
            </SoftBox>
            <Grid container spacing={2}>
              <Grid item xs={4} sm={4}>
                <InputLabel htmlFor="nombre">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Tipo de usuario
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <Select name="tipo" value={tipoClientes} onChange={handleChangeTipo}>
                  <MenuItem value={"Comedor"}>Comedor</MenuItem>
                  <MenuItem value={"Alumno"}>Alumno</MenuItem>
                  <MenuItem value={"UPrO Compras"}>UPrO Compras</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={8}>
                <InputLabel htmlFor="cliente">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Buscar por Nombre / DNI
                  </SoftTypography>
                </InputLabel>
                <Autocomplete
                  clearOnEscape
                  options={!debouncing ? clientes : []}
                  value={cliente ? cliente : null}
                  loading={debouncing}
                  loadingText="Buscando..."
                  noOptionsText={
                    search.length > 1 ? "No se encontraron resultados" : "Escriba para buscar"
                  }
                  onInputChange={handleSearchChange}
                  getOptionLabel={(option) =>
                    option?.nombre
                      ? option.nombre +
                        " " +
                        option.apellido +
                        (option.dni ? " - Dni:" + option.dni : "")
                      : ""
                  }
                  filterOptions={(options, params) => {
                    const filtered = filterOptions(options, params);
                    return filtered;
                  }}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setCliente(newValue);
                    }
                  }}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        slotprops={{
                          input: {
                            ...params.InputProps,
                            type: "search",
                          },
                        }}
                      />
                    );
                  }}
                />
              </Grid>
            </Grid>
            <SoftBox
              display="flex"
              justifyContent="end"
              mt={2}
              gap={1}
              flexDirection={{ xs: "column", sm: "row" }}
            >
              <SoftButton color="uproGreen" circular onClick={handleClose} disabled={loading}>
                <Icon sx={{ fontWeight: "light" }}>close</Icon>
                &nbsp;Cerrar
              </SoftButton>
              <SoftButton
                color="uproPink"
                circular
                disabled={loading || !cliente}
                onClick={() => {
                  saveUsuarioVip({
                    variables: {
                      input: {
                        idUsuariosVip: cliente.id,
                        sistema:
                          tipoClientes === "Comedor"
                            ? "Comedor"
                            : tipoClientes === "Alumno"
                            ? "Alumno"
                            : "Compras",
                        nombre: cliente.nombre + " " + cliente.apellido,
                      },
                    },
                    onCompleted: () => {
                      handleSnackbar("Usuario vip agregado correctamente", "success");
                      handleClose();
                      refetch();
                    },
                    onError: (error) => {
                      handleSnackbar(error.message, "error");
                    },
                  });
                }}
              >
                <Icon sx={{ fontWeight: "light" }}>save</Icon>
                &nbsp;Guardar
              </SoftButton>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalAdd.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  data: PropTypes.object,
  refetch: PropTypes.func,
};
